import { filter } from 'lodash';
import { EEventAgeGroup } from 'data/types/events.types';
import { useTranslation } from '@packages/utils';
import { stepThreeKey } from '../../RegistrationModal/RegistrationModal.data';
import { AgeGroupCapacitiesType } from '../context/NewRegistrationModalContext';

export const convertToChildArray = (input: any) => {
  const result: {
    ageGroup: EEventAgeGroup;
    name: string;
    showWarning: boolean;
  }[] = [];

  // Iterate through the keys of the object
  Object.keys(input).forEach(key => {
    const match = key.match(/child-(ageGroup|name)-(\d+)/);
    if (match) {
      const [, field, index] = match; // Extract field (age or name) and index
      // @ts-ignore
      if (!result[index]) result[index] = {}; // Initialize the object if it doesn't exist
      // @ts-ignore
      result[index][field] = input[key]; // Assign the value to the corresponding field
    }
  });

  return result;
};

export const convertToChildObject = (
  inputArray: { ageGroup: string; name: string }[],
) => {
  const result: any = {};

  inputArray.forEach((child, index: number) => {
    if (child.ageGroup) {
      result[`child-ageGroup-${index}`] = child.ageGroup;
    }
    if (child.name) {
      result[`child-name-${index}`] = child.name;
    }
  });

  return result;
};

export const convertToAdultArray = (input: any) => {
  const result: {
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
    ageGroup: string;
  }[] = [];

  // Iterate through the keys of the object
  Object.keys(input).forEach(key => {
    const match = key.match(
      /adult-(firstName|lastName|email|mobile|ageGroup)-(\d+)/,
    );
    if (match) {
      const [, field, index] = match; // Extract field (age or name) and index
      // @ts-ignore
      if (!result[index]) result[index] = {}; // Initialize the object if it doesn't exist
      // @ts-ignore
      result[index][field] = input[key]; // Assign the value to the corresponding field
    }
  });

  return result;
};

export const parseAdultDetails = (inputObject: any): any[] => {
  const adults: Record<number, any> = {};

  Object.keys(inputObject).forEach(key => {
    const value = inputObject[key];

    // Match main adult details
    const adultMatch = key.match(
      /^adult-(firstName|lastName|email|mobile|ageGroup)-(\d+)$/,
    );
    if (adultMatch) {
      const [, field, index] = adultMatch;
      const adultIndex = Number(index);
      if (!adults[adultIndex]) {
        adults[adultIndex] = {
          firstName: '',
          lastName: '',
          email: '',
          mobile: '',
          ageGroup: '',
          additionalAdults: [],
        };
      }
      adults[adultIndex][field as keyof Omit<any, 'additionalAdults'>] = value;
      return;
    }

    // Match additional adult details
    const additionalMatch = key.match(
      /^additional-adult-(firstName|lastName|email|mobile|ageGroup)-(\d+)-(\d+)$/,
    );
    if (additionalMatch) {
      const [, field, adultIndex, additionalIndex] = additionalMatch;
      const mainIndex = Number(adultIndex);
      const subIndex = Number(additionalIndex);

      if (!adults[mainIndex]) {
        adults[mainIndex] = {
          firstName: '',
          lastName: '',
          email: '',
          mobile: '',
          ageGroup: '',
          additionalAdults: [],
        };
      }
      if (!adults[mainIndex].additionalAdults[subIndex]) {
        adults[mainIndex].additionalAdults[subIndex] = {
          firstName: '',
          lastName: '',
          email: '',
          mobile: '',
          ageGroup: '',
        };
      }

      adults[mainIndex].additionalAdults[subIndex][
        field as keyof any['additionalAdults'][0]
      ] = value;
    }
  });

  return Object.values(adults);
};

export const convertToAdultObject = (
  inputArray: {
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
    ageGroup: string;
  }[],
) => {
  const result: any = {};

  inputArray.forEach((adult, index: number) => {
    if (adult.ageGroup) {
      result[`adult-ageGroup-${index}`] = adult.ageGroup;
    }
    if (adult.firstName) {
      result[`adult-firstName-${index}`] = adult.firstName;
    }
    if (adult.lastName) {
      result[`adult-lastName-${index}`] = adult.lastName;
    }
    if (adult.email) {
      result[`adult-email-${index}`] = adult.email;
    }
    if (adult.mobile) {
      result[`adult-mobile-${index}`] = adult.mobile;
    }
  });

  return result;
};

export const convertAdultsToObject = (
  adults: any[],
): Record<string, string> => {
  const outputObject: Record<string, string> = {};

  adults.forEach((adult, adultIndex) => {
    outputObject[`adult-firstName-${adultIndex}`] = adult.firstName;
    outputObject[`adult-lastName-${adultIndex}`] = adult.lastName;
    outputObject[`adult-email-${adultIndex}`] = adult.email;
    outputObject[`adult-mobile-${adultIndex}`] = adult.mobile;
    outputObject[`adult-ageGroup-${adultIndex}`] = adult.ageGroup;

    adult.additionalAdults.forEach(
      (additional: any, additionalIndex: number) => {
        outputObject[
          `additional-adult-firstName-${adultIndex}-${additionalIndex}`
        ] = additional.firstName;
        outputObject[
          `additional-adult-lastName-${adultIndex}-${additionalIndex}`
        ] = additional.lastName;
        outputObject[
          `additional-adult-email-${adultIndex}-${additionalIndex}`
        ] = additional.email;
        outputObject[
          `additional-adult-mobile-${adultIndex}-${additionalIndex}`
        ] = additional.mobile;
        outputObject[
          `additional-adult-ageGroup-${adultIndex}-${additionalIndex}`
        ] = adult.ageGroup;
      },
    );
  });

  return outputObject;
};

export const JoinWithAnd = (arr: string[]) => {
  const { t: tStepThree } = useTranslation({
    keyPrefix: stepThreeKey,
    lng: 'translate',
  });

  if (arr.length === 0) return '';
  if (arr.length === 1) return arr[0];
  const lastItem = arr.pop(); // Remove the last item
  return `${arr.join(', ')} ${tStepThree('AND')} ${lastItem}`;
};

export const filterFullGroups = (obj: AgeGroupCapacitiesType) => {
  if (!obj) return []; // Handle undefined case

  // Use lodash to filter the object entries
  return filter(Object.entries(obj), ([, value]) => value.full).map(
    ([key, value]) => ({ key, ...value }),
  );
};
